// Scoping and bucketing for an agent's own book of business.
//
// OMK has no agent codes: the embedded digital journeys stamp the acting agent
// onto additional_details (agent, agent_email, and since the agent_guid change,
// agent_guid). These helpers read that stamp back out.

export const NEW_BUSINESS = 'new-business'
export const PORTFOLIO = 'portfolio'

// Submitted but not yet accepted. Rejected belongs here too — the agent needs
// to see that a submission did not make it, and it is not part of their book.
//
// Portfolio is deliberately defined as "not one of these" rather than its own
// list, so a status nobody anticipated surfaces somewhere instead of vanishing.
const NEW_BUSINESS_STATUSES = [
  'Un_Confirmed',
  'Pending',
  'Unpaid',
  'Rejected'
]

function additionalDetails(policy) {
  return (policy && policy.additional_details) || {}
}

export function isNewBusiness(policy) {
  const status = String((policy && policy.status) || '')
  return NEW_BUSINESS_STATUSES.indexOf(status) !== -1
}

export function isPortfolio(policy) {
  return !!policy && !isNewBusiness(policy)
}

// The query that scopes the policies endpoint to one agent.
//
// additional_details is a passthrough blob on the filter body, matched the same
// way aml_flagged / pep_flagged / iprs_flagged already are, and agent_guid is
// where the digital journeys stamp the acting agent.
//
// NOTE: policies onboarded before the journeys started sending agent_guid carry
// only agent/agent_email, so the engine will not return them. They need a
// backfill to appear in an agent's views.
export function agentPolicyFilter(agentGuid) {
  return { additional_details: { agent_guid: agentGuid } }
}

// Does this policy belong to the given agent?
//
// The engine already narrows on agent_guid, so this is a second line of
// defence: if the filter is ever ignored or loosened server-side, another
// agent's policy still cannot reach the table. A display name and an email are
// neither unique nor stable, so only the guid is matched on.
export function belongsToAgent(policy, agentGuid) {
  const details = additionalDetails(policy)
  if (!agentGuid || !details.agent_guid) {
    return false
  }
  return details.agent_guid === agentGuid
}

// QA sent the policy back to the capturing agent asking for something. The
// policy-processing "Request changes" action is what sets this.
export function hasRequirementPending(policy) {
  return additionalDetails(policy).need_action === true
}

// The agent's policies for one view, newest first.
export function selectAgentPolicies(policies, view, agentGuid) {
  const inView = view === NEW_BUSINESS ? isNewBusiness : isPortfolio

  return (policies || [])
    .filter(function(policy) {
      return belongsToAgent(policy, agentGuid) && inView(policy)
    })
    .sort(function(a, b) {
      return new Date(b.created_at) - new Date(a.created_at)
    })
}
